/**
 * The Admin's risk areas, alert center and individual alert (W9-RISK-AREAS-ALERTS, D474, ADR-0092; DevMobility C-024, I-076,
 * H-009, H-010, H-011, H-012, I-058) — framework-neutral view-models.
 *
 *  - **Operações › Áreas de risco** (`risk_areas`): the market's areas, each with its in-force version and any version waiting
 *    for a decision; the editor turns what the operator draws (tapped points on the map, or typed coordinates) into a proposal
 *    — a polygon of at least three points or a circle, a description the driver reads, the window, local-time windows in the
 *    market's zone, the categories, the active switch. ANOTHER operator approves it; the console never offers the maker his own
 *    approval (the server refuses it too).
 *  - **Operações › Central de alertas** (`alert_center`): the platform's valid alerts with their unread count, the catalog and
 *    thresholds in force (edited in the governed-settings console, four eyes) and the individual alert to one rider or driver.
 *
 * The console only presents: the server authorizes every call at the market asked for. Neutral: no country, zone, currency or
 * language is named here; amounts arrive as integer minor units and render through `@vello/format`.
 */
import type {
  PlatformAlertView,
  RiskAreaDefinitionView,
  RiskAreaGeometryView,
  RiskAreaProposalInput,
  RiskAreaRecordView,
  ScheduleDayView,
} from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { zonedLocalToUtc } from './market-time.js';

// ---- authority (display only — the server re-authorizes every call) ------------------------------------------------

export function riskAreaAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return { mayView: grantsAllow(grants, { permission: 'safety:view', target: 'GEO' }), mayManage: grantsAllow(grants, { permission: 'safety:manage', target: 'GEO' }) };
}

export function alertCenterAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean; readonly mayWrite: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false, mayWrite: false };
  return {
    mayView: grantsAllow(grants, { permission: 'alert:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'alert:manage', target: 'GEO' }),
    // The individual alert is a platform notification (`notification:send`, global grant).
    mayWrite: grantsAllow(grants, { permission: 'notification:send', target: 'PLATFORM' }),
  };
}

// ---- the list of areas ---------------------------------------------------------------------------------------------

export interface RiskAreaRow {
  readonly areaId: string;
  /** The newest version of any status (what the row is named by). */
  readonly latest: RiskAreaRecordView;
  /** The version in force: the newest APPROVED one. */
  readonly approved?: RiskAreaRecordView;
  /** The version waiting for a decision, if any (at most one per area). */
  readonly pending?: RiskAreaRecordView;
  readonly versions: number;
}

/**
 * Group the versions of a market by area, areas newest first; each carries its approved and its pending version. With `at`, the
 * approved version is the one IN EFFECT then (an approved version dated in the future does not replace the earlier one before
 * it starts — the same rule the wave planner reads with).
 */
export function groupRiskAreas(records: readonly RiskAreaRecordView[], at?: string): readonly RiskAreaRow[] {
  const byArea = new Map<string, RiskAreaRecordView[]>();
  for (const r of records) byArea.set(r.definition.areaId, [...(byArea.get(r.definition.areaId) ?? []), r]);
  const rows: RiskAreaRow[] = [];
  for (const [areaId, versions] of byArea) {
    const sorted = [...versions].sort((a, b) => b.definition.version - a.definition.version);
    const inEffect = at === undefined ? undefined : sorted.find((v) => v.status === 'APPROVED' && Date.parse(v.definition.effectiveFrom) <= Date.parse(at));
    const approved = inEffect ?? sorted.find((v) => v.status === 'APPROVED');
    const pending = sorted.find((v) => v.status === 'PROPOSED');
    rows.push({ areaId, latest: sorted[0] as RiskAreaRecordView, ...(approved !== undefined ? { approved } : {}), ...(pending !== undefined ? { pending } : {}), versions: sorted.length });
  }
  return rows.sort((a, b) => b.latest.proposedAt.localeCompare(a.latest.proposedAt) || a.areaId.localeCompare(b.areaId));
}

/** Whether the area marks offers now: it has an approved version, that version is active, and its window is open at `at`. */
export function areaInForce(row: RiskAreaRow, at: string): boolean {
  const d = row.approved?.definition;
  if (d === undefined || !d.active) return false;
  const t = Date.parse(at);
  return Date.parse(d.effectiveFrom) <= t && (d.effectiveTo === undefined || t < Date.parse(d.effectiveTo));
}

export type RiskAreaTone = 'neutral' | 'success' | 'warning' | 'danger';
export function riskStatusTone(status: RiskAreaRecordView['status']): RiskAreaTone {
  return status === 'APPROVED' ? 'success' : status === 'PROPOSED' ? 'warning' : 'danger';
}

/** Why the signed-in operator cannot decide a version (undefined → may decide). */
export function decideBlock(record: RiskAreaRecordView, operatorId: string | undefined, mayManage: boolean, selfApproval = false): 'NO_PERMISSION' | 'NOT_PENDING' | 'OWN_PROPOSAL' | undefined {
  if (!mayManage) return 'NO_PERMISSION';
  if (record.status !== 'PROPOSED') return 'NOT_PENDING';
  if (!selfApproval && operatorId !== undefined && record.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

// ---- the editor ----------------------------------------------------------------------------------------------------

export const SCHEDULE_DAYS: readonly ScheduleDayView[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export interface RiskAreaDraft {
  readonly areaId?: string;
  readonly description: string;
  readonly shape: 'polygon' | 'circle';
  /** The polygon's points as the operator tapped or typed them, in order. */
  readonly ring: readonly { readonly latitude: number; readonly longitude: number }[];
  readonly center?: { readonly latitude: number; readonly longitude: number };
  readonly radiusMeters: string;
  /** `datetime-local` values typed in the MARKET's time (empty `to` = open-ended). */
  readonly from: string;
  readonly to: string;
  /** One daily window in the market's zone; empty strings = always. */
  readonly daysOpen: readonly ScheduleDayView[];
  readonly dailyStart: string;
  readonly dailyEnd: string;
  readonly categories: string;
  readonly active: boolean;
}

export const EMPTY_DRAFT: RiskAreaDraft = { description: '', shape: 'polygon', ring: [], radiusMeters: '', from: '', to: '', daysOpen: [], dailyStart: '', dailyEnd: '', categories: '', active: true };

export type DraftIssue =
  | 'DESCRIPTION_REQUIRED'
  | 'TOO_FEW_POINTS'
  | 'CENTER_REQUIRED'
  | 'RADIUS_INVALID'
  | 'FROM_REQUIRED'
  | 'WINDOW_INVALID'
  | 'DAILY_WINDOW_INVALID'
  | 'CATEGORIES_INVALID';

/** Parse a typed coordinate pair (`lat, lng`) — undefined when it is not a valid pair. */
export function parseLatLng(text: string): { readonly latitude: number; readonly longitude: number } | undefined {
  const m = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(text);
  if (m === null) return undefined;
  const latitude = Number(m[1]);
  const longitude = Number(m[2]);
  return Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180 ? { latitude, longitude } : undefined;
}

/** Round a tapped point to 6 decimals (about 0.1 m), so the stored geometry is stable and readable. */
export function roundPoint(p: { readonly latitude: number; readonly longitude: number }): { readonly latitude: number; readonly longitude: number } {
  const r = (n: number): number => Math.round(n * 1e6) / 1e6;
  return { latitude: r(p.latitude), longitude: r(p.longitude) };
}

/**
 * Turn the draft into the proposal the server takes, with the market's local wall-clock entries converted to UTC. The
 * first issue stops it (the editor names it); the server validates the content again.
 */
export function draftToProposal(draft: RiskAreaDraft, timeZone: string | undefined): { readonly ok: true; readonly input: RiskAreaProposalInput } | { readonly ok: false; readonly issue: DraftIssue } {
  const description = draft.description.trim();
  if (description === '') return { ok: false, issue: 'DESCRIPTION_REQUIRED' };
  let geometry: RiskAreaGeometryView;
  if (draft.shape === 'polygon') {
    if (draft.ring.length < 3) return { ok: false, issue: 'TOO_FEW_POINTS' };
    geometry = { kind: 'polygon', ring: draft.ring.map(roundPoint) };
  } else {
    if (draft.center === undefined) return { ok: false, issue: 'CENTER_REQUIRED' };
    const radius = Number(draft.radiusMeters);
    if (!Number.isFinite(radius) || radius < 10 || radius > 100_000) return { ok: false, issue: 'RADIUS_INVALID' };
    geometry = { kind: 'circle', center: roundPoint(draft.center), radiusMeters: Math.round(radius) };
  }
  if (draft.from.trim() === '') return { ok: false, issue: 'FROM_REQUIRED' };
  const tz = timeZone ?? 'UTC';
  const effectiveFrom = zonedLocalToUtc(draft.from, tz);
  const effectiveTo = draft.to.trim() === '' ? undefined : zonedLocalToUtc(draft.to, tz);
  if (effectiveFrom === undefined || (draft.to.trim() !== '' && effectiveTo === undefined)) return { ok: false, issue: 'WINDOW_INVALID' };
  if (effectiveTo !== undefined && Date.parse(effectiveTo) <= Date.parse(effectiveFrom)) return { ok: false, issue: 'WINDOW_INVALID' };
  let schedule: RiskAreaDefinitionView['schedule'];
  const daily = draft.daysOpen.length > 0 || draft.dailyStart !== '' || draft.dailyEnd !== '';
  if (daily) {
    if (draft.daysOpen.length === 0 || !/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.dailyStart) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.dailyEnd) || draft.dailyStart >= draft.dailyEnd) return { ok: false, issue: 'DAILY_WINDOW_INVALID' };
    schedule = { windows: [{ days: [...draft.daysOpen], start: draft.dailyStart, end: draft.dailyEnd }] };
  }
  const categoryCodes = draft.categories.split(',').map((c) => c.trim()).filter((c) => c !== '');
  if (categoryCodes.some((c) => !/^[A-Za-z0-9_-]{1,40}$/.test(c)) || new Set(categoryCodes).size !== categoryCodes.length || categoryCodes.length > 20) return { ok: false, issue: 'CATEGORIES_INVALID' };
  return {
    ok: true,
    input: {
      ...(draft.areaId !== undefined ? { areaId: draft.areaId } : {}),
      description,
      geometry,
      categoryCodes,
      effectiveFrom,
      ...(effectiveTo !== undefined ? { effectiveTo } : {}),
      ...(schedule !== undefined ? { schedule } : {}),
      active: draft.active,
    },
  };
}

/** A draft that edits an existing version (its points, window and switch), the new version being proposed under the same area. */
export function draftFromDefinition(d: RiskAreaDefinitionView, toLocal: (utc: string) => string | undefined): RiskAreaDraft {
  const g = d.geometry as RiskAreaGeometryView;
  const w = d.schedule?.windows[0];
  return {
    areaId: d.areaId,
    description: d.description,
    shape: g.kind === 'circle' ? 'circle' : 'polygon',
    ring: g.kind === 'polygon' ? g.ring : [],
    ...(g.kind === 'circle' ? { center: g.center } : {}),
    radiusMeters: g.kind === 'circle' ? String(g.radiusMeters) : '',
    from: toLocal(d.effectiveFrom) ?? '',
    to: d.effectiveTo !== undefined ? toLocal(d.effectiveTo) ?? '' : '',
    daysOpen: w?.days ?? [],
    dailyStart: w?.start ?? '',
    dailyEnd: w?.end ?? '',
    categories: d.categoryCodes.join(', '),
    active: d.active,
  };
}

/** The outline of an area for the map: a polygon's ring, or a circle sampled into 48 points. */
export function outlineOf(g: RiskAreaDefinitionView['geometry']): readonly { readonly latitude: number; readonly longitude: number }[] {
  const geo = g as RiskAreaGeometryView;
  if (geo.kind === 'polygon') return geo.ring.length >= 3 ? [...geo.ring, geo.ring[0] as { latitude: number; longitude: number }] : geo.ring;
  if (geo.kind !== 'circle') return [];
  const out: { latitude: number; longitude: number }[] = [];
  const dLat = geo.radiusMeters / 111_320;
  const dLng = geo.radiusMeters / (111_320 * Math.max(0.01, Math.cos((geo.center.latitude * Math.PI) / 180)));
  for (let i = 0; i <= 48; i += 1) {
    const a = (i / 48) * 2 * Math.PI;
    out.push({ latitude: geo.center.latitude + dLat * Math.sin(a), longitude: geo.center.longitude + dLng * Math.cos(a) });
  }
  return out;
}

// ---- the alert center ----------------------------------------------------------------------------------------------

/** The i18n key of an alert type's name. */
export const alertTypeKey = (type: string): string => `alert.type.${type}`;

export function alertTone(type: string): RiskAreaTone {
  return type === 'DRIVER_PANIC' || type === 'CHARGEBACK_DETECTED' ? 'danger' : type === 'RIDE_IN_RISK_AREA' || type.startsWith('HIGH_') ? 'warning' : type === 'RIDER_DEBT_SETTLED' ? 'success' : 'neutral';
}

/** Where an alert's subject lives in the console (a ride's page), when it names one. */
export function alertSubjectRoute(alert: PlatformAlertView): { readonly route: 'ride_detail'; readonly rideId: string } | undefined {
  if (alert.subjectRef === undefined) return undefined;
  return alert.type === 'HIGH_ESTIMATED_FARE' || alert.type === 'HIGH_FINAL_FARE' || alert.type === 'RIDE_IN_RISK_AREA' ? { route: 'ride_detail', rideId: alert.subjectRef } : undefined;
}

/** An integer minor-unit amount from an alert's render parameters (decimal string or number), or undefined. */
export function alertAmountMinor(alert: PlatformAlertView, key: string): bigint | undefined {
  const v = alert.params[key];
  if (typeof v === 'number') return Number.isInteger(v) ? BigInt(v) : undefined;
  return typeof v === 'string' && /^-?\d+$/.test(v) ? BigInt(v) : undefined;
}
